import React from 'react';
import { Snackbar, SnackbarOrigin, SnackbarProps } from '@mui/material';
import { CustomIcon } from 'components/CustomIcon';
import { IconType } from 'components/CustomIcon/CustomIcon.config';
import palette from 'theme/palette';
import * as Styled from './MBSnackbar.styles';
import { Button, IconButton } from '../../Buttons';

export enum StatusEnum {
  SUCCESS = 'success',
  DANGER = 'danger',
  WARNING = 'warning',
  DEFAULT = 'default',
  WHITE = 'white',
}

const StatusBgColor = {
  default: palette.primary.main,
  success: palette.green.main,
  warning: palette.warning.main,
  danger: palette.error.main,
  white: palette.common.white,
};

export type MBSnackbarProps = {
  id: string;
  message: string | React.ReactNode;
  iconsColor?: string;
  startIcon?: IconType;
  startIconSize?: number;
  buttonText?: string;
  buttonEndIcon?: IconType;
  buttonEndSize?: number;
  status?: StatusEnum;
  onButtonClick?: (id: string) => void;
} & SnackbarProps;

const defaultAnchorOrigin: SnackbarOrigin = {
  vertical: 'top',
  horizontal: 'center',
};

const MBSnackbar: React.FC<MBSnackbarProps> = ({
  id,
  message,
  buttonText,
  buttonEndIcon = 'close',
  buttonEndSize = 0.8,
  startIcon,
  startIconSize = 1.2,
  iconsColor = palette.common.white,
  anchorOrigin = defaultAnchorOrigin,
  status = StatusEnum.DEFAULT,
  onButtonClick = null,
  ...rest
}) => {
  const hasButton = onButtonClick != null && (buttonText || buttonEndIcon);
  const hasOnlyIcon = buttonEndIcon != null && !buttonText;
  const isDarkColor =
    status === StatusEnum.WARNING || status === StatusEnum.WHITE;
  const snackbarIconColor = isDarkColor ? palette.primary.main : iconsColor;
  const snackbarTextColor = isDarkColor
    ? palette.primary.main
    : palette.common.white;

  return (
    <Snackbar id={id} anchorOrigin={anchorOrigin} {...rest}>
      <Styled.ContentContainer
        bgColor={StatusBgColor[status]}
        iconsColor={snackbarIconColor}
        endIconSize={buttonEndSize}
      >
        {startIcon != null && (
          <CustomIcon
            size={startIconSize}
            dense
            className="startIcon"
            name={startIcon}
          />
        )}
        <Styled.TextBox px={1} color={snackbarTextColor}>
          {message}
        </Styled.TextBox>
        {hasButton ? (
          hasOnlyIcon ? (
            <IconButton
              dense
              className="endIcon"
              onClick={() => onButtonClick?.(id)}
              iconName={buttonEndIcon}
            />
          ) : (
            <Button
              size="small"
              color="secondary"
              iconDense
              onClick={() => onButtonClick?.(id)}
              variant="contained"
              endIcon={buttonEndIcon}
              endIconSize={buttonEndSize}
            >
              {buttonText}
            </Button>
          )
        ) : null}
      </Styled.ContentContainer>
    </Snackbar>
  );
};

export default MBSnackbar;
